import { useState } from 'react';
import WizardModal, { WizardButton, WizardError } from './WizardModal';
import { post } from '../../api';
// Nico replaces Flora

interface SnackSuggestion {
  name: string;
  description: string;
  nutrition: {
    kcal: number;
    protein_g: number;
    fat_g: number;
    carbs_g: number;
  };
  remaining_kcal: number;
  remaining_protein_g: number;
}

type Step = 'idle' | 'loading' | 'suggestion' | 'tracked';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function SnackWizard({ open, onClose }: Props) {
  const [step, setStep] = useState<Step>('idle');
  const [suggestion, setSuggestion] = useState<SnackSuggestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tracking, setTracking] = useState(false);

  const handleGenerate = async () => {
    setStep('loading');
    setError(null);
    try {
      const data = await post<SnackSuggestion>('/ai/suggest-snack', {});
      setSuggestion(data);
      setStep('suggestion');
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Vorschlag fehlgeschlagen');
      setStep('idle');
    }
  };

  const handleTrack = async () => {
    if (!suggestion) return;
    setTracking(true);
    try {
      await post('/tracking', {
        meal_type: 'snack',
        description: suggestion.name,
        kcal: suggestion.nutrition.kcal,
        protein_g: suggestion.nutrition.protein_g,
        fat_g: suggestion.nutrition.fat_g,
        carbs_g: suggestion.nutrition.carbs_g,
      });
      setStep('tracked');
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Tracking fehlgeschlagen');
    } finally {
      setTracking(false);
    }
  };

  const handleClose = () => {
    setStep('idle');
    setSuggestion(null);
    setError(null);
    onClose();
  };

  const handleAnother = () => {
    setSuggestion(null);
    setError(null);
    handleGenerate();
  };

  // ── Tracked ──
  if (step === 'tracked') {
    return (
      <WizardModal open={open} onClose={handleClose} title="Snack getrackt!" footer={null}>
        <div className="text-center py-4">
          <img src="/nico-transparent.svg" alt="Nico" className="w-16 h-16 mx-auto" />
          <p className="text-sm text-gray-700 font-medium mt-3">{suggestion?.name} getrackt!</p>
          <div className="flex gap-2 justify-center mt-4">
            <button onClick={handleClose} className="px-4 py-2 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors">
              Fertig
            </button>
            <button onClick={handleAnother} className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-xl text-sm font-medium hover:bg-emerald-200 transition-colors">
              Noch ein Vorschlag
            </button>
          </div>
        </div>
      </WizardModal>
    );
  }

  // ── Suggestion ──
  if (step === 'suggestion' && suggestion) {
    return (
      <WizardModal
        open={open}
        onClose={handleClose}
        title="Snack-Vorschlag"
        footer={
          <div className="flex gap-2">
            <button
              onClick={handleAnother}
              className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl font-medium text-sm hover:bg-gray-200 transition-colors"
            >
              Anderer Vorschlag
            </button>
            <WizardButton onClick={handleTrack} loading={tracking}>
              Tracken
            </WizardButton>
          </div>
        }
      >
        {error && <WizardError message={error} />}

        <div className="text-center">
          <img src="/nico-transparent.svg" alt="Nico" className="w-14 h-14 mx-auto" />
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h3 className="font-semibold text-gray-800">{suggestion.name}</h3>
          <p className="text-sm text-gray-500 mt-1">{suggestion.description}</p>
        </div>

        <div className="grid grid-cols-4 gap-2">
          <NutritionChip label="kcal" value={suggestion.nutrition.kcal} color="emerald" />
          <NutritionChip label="Protein" value={`${suggestion.nutrition.protein_g}g`} color="blue" />
          <NutritionChip label="Fett" value={`${suggestion.nutrition.fat_g}g`} color="orange" />
          <NutritionChip label="Carbs" value={`${suggestion.nutrition.carbs_g}g`} color="purple" />
        </div>

        <p className="text-xs text-gray-400 text-center">
          Restbudget: {suggestion.remaining_kcal} kcal, {suggestion.remaining_protein_g}g Protein
        </p>
      </WizardModal>
    );
  }

  // ── Idle / Loading ──
  return (
    <WizardModal
      open={open}
      onClose={handleClose}
      title="Snack-Idee"
      footer={
        <WizardButton onClick={handleGenerate} loading={step === 'loading'}>
          Vorschlag generieren
        </WizardButton>
      }
    >
      {error && <WizardError message={error} onRetry={handleGenerate} />}

      <div className="text-center py-4">
        <img src="/nico-transparent.svg" alt="Nico" className="w-16 h-16 mx-auto" />
        <p className="text-sm text-gray-500 mt-3">
          Nico schlägt dir einen Snack vor, der in dein Restbudget passt.
        </p>
      </div>
    </WizardModal>
  );
}

function NutritionChip({ label, value, color }: { label: string; value: string | number; color: string }) {
  const colors: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-700',
    blue: 'bg-blue-50 text-blue-700',
    orange: 'bg-orange-50 text-orange-700',
    purple: 'bg-purple-50 text-purple-700',
  };
  return (
    <div className={`rounded-lg px-2 py-1.5 text-center ${colors[color]}`}>
      <p className="text-sm font-semibold">{value}</p>
      <p className="text-[10px] opacity-70">{label}</p>
    </div>
  );
}
